import { useStateMachine } from "little-state-machine";
import { Accordion, Button, Form } from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import { updateUserFilter } from "actions/updateUserFilter";
import { useForm } from "react-hook-form";
import { Divider, NoData } from "@whitespace/whitespace-ui";
import { updateIsUserFilterDrawerOpen } from "actions/updateIsUserFilterDrawerOpen";

import { useCallback, useEffect, useState } from "react";
import IUserFilter from "interfaces/IUserFilter";
import { initialUserFilter } from "modules/InitialUserFilter";
import GroupsFilter from "components/FilterSections/GroupsFilter";
import { UserType, UserTypeDisplayValue } from "interfaces/models/UserType";
import IDrawer from "interfaces/IDrawer";

const UserFilterDrawer = ({ isOpen, onClose }: IDrawer) => {
  const { actions } = useStateMachine({
    updateUserFilter,
    updateIsUserFilterDrawerOpen
  });

  const { state } = useStateMachine();
  const [hasUserTypeFilterChanged, setHasUserTypeFilterChanged] = useState(false);
  const [hasGroupsFilterChanged, setHasGroupsFilterChanged] = useState(false);
  const [accordionKey, setAccordionKey] = useState(0);
  const [applyFilterButtonDisabled, setApplyFilterButtonIsDisabled] = useState(true);
  const {
    register, handleSubmit, getValues, setValue
  } = useForm({
    mode: "onSubmit"
  });

  const constructFilterFromForm = useCallback(() => {
    const userFilter = initialUserFilter();

    const userTypesSelected = getValues("userType") as UserType[];

    if (userTypesSelected) {
      if (typeof userTypesSelected === "string") {
        userFilter.userTypes.push(userTypesSelected);
      } else {
        userFilter.userTypes = userTypesSelected;
      }
    }
    const groupsSelected = getValues("group") as string;
    if (groupsSelected) {
      userFilter.group = groupsSelected;
    }
    return userFilter;
  }, [getValues]);

  const handleGroupSelectionChange = (e? : any) => {
    if (e) {
      setValue("group", e.currentTarget.value);
    }
    const filter = constructFilterFromForm();
    setApplyFilterButtonIsDisabled(!(filter.group || filter.userTypes.length > 0));
  };

  const handleUserTypeSelectionChange = () => {
    const filter = constructFilterFromForm();
    setApplyFilterButtonIsDisabled(!(filter.group || filter.userTypes.length > 0));
  };

  const handleApplyFilterClick = () => {
    const userFilter = { ...state.userFilter };
    const drawerFilter = constructFilterFromForm();
    userFilter.userTypes = drawerFilter.userTypes ?? [];
    userFilter.group = drawerFilter.group ?? undefined;

    actions.updateUserFilter(userFilter);
    onClose();
  };

  const handleResetClick = () => {
    const userFilter = { ...state.userFilter };
    // userFilter.group = undefined;
    userFilter.userTypes = [];
    actions.updateUserFilter(userFilter);
    onClose();
  };

  const renderUserTypes = () => state.userTypes?.map(userType => {
    if (state.userTypes!.includes(userType)) {
      return (
        <Form.Check
          {...register("userType")}
          key={`userType_${userType}`}
          id={`userType_${userType}`}
          data-testid={`userType_${userType}`}
          name="userType"
          type="checkbox"
          label={UserTypeDisplayValue[userType]}
          value={userType}
          className="left-margin-medium"
          onInput={handleUserTypeSelectionChange}
        />
      );
    }
    return <></>;
  });

  const className = `offcanvas offcanvas-end ${isOpen ? "show" : ""}`;
  const backdropClassName = `backdrop ${!isOpen ? "hidden" : ""}`;
  const tabIndex = isOpen ? 0 : -1;

  const setControlValues = useCallback((userFilter : IUserFilter) => {
    if (state.isUserFilterDrawerOpen) {
      setValue("userType", userFilter.userTypes.length > 0 ? userFilter.userTypes.map(String) : "");
      setValue("group", userFilter.group ? userFilter.group : undefined);
      handleGroupSelectionChange();
      handleUserTypeSelectionChange();
    }
  }, [setValue, state.isUserFilterDrawerOpen]);

  useEffect(() => {
    if (state.isUserFilterDrawerOpen) {
      setAccordionKey(new Date().getTime());
      setHasUserTypeFilterChanged(state.userFilter !== undefined && state.userFilter.userTypes.length > 0);
      setHasGroupsFilterChanged(true);
    } else {
      setValue("groupSearch", "");
    }
  }, [state.isUserFilterDrawerOpen]);

  useEffect(() => {
    setControlValues(state.userFilter!);
  }, [setControlValues]);

  // Awaiting Refactor for accessibility.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <FocusTrap active={process.env.REACT_APP_AMPLIFY_TEST_MODE === "true" ? false : isOpen}>
      <div className="filter-drawer" data-testid="drawer">
        <div className={backdropClassName} onClick={onClose} />

        <Form noValidate onSubmit={handleSubmit(handleApplyFilterClick)}>
          <div className={className} id="offcanvasRight">
            <section>
              <div className="d-flex m-3">
                <h2 className="w-100 m-0" data-testid="filter-header">User Filter</h2>
                <Button type="button" className="btn-close" data-testid="btnUserFilterClose" onClick={onClose} tabIndex={tabIndex} aria-label="Close" />
              </div>
              <Divider />
              <div className="d-flex justify-content-space-between align-content-center">
                <div className="m-3">
                  <Button
                    type="submit"
                    variant="primary"
                    tabIndex={tabIndex}
                    style={{ marginRight: "1em" }}
                    disabled={applyFilterButtonDisabled}
                  >
                    Apply Filter
                  </Button>
                  <Button
                    data-testid="reset-button"
                    variant="secondary"
                    onClick={handleResetClick}
                    tabIndex={tabIndex}
                    disabled={state.userFilter.userTypes.length === 0}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </section>
            <Divider />
            {(isOpen)
            && (
              <>
                {(state.groupsFilteredUsers?.length! > 1) && (
                  <>
                    <GroupsFilter
                      accordionKey={`userGroup_${accordionKey}`}
                      register={register}
                      hasFilterChanged={hasGroupsFilterChanged}
                      groups={state.groupsFilteredUsers!}
                      handleOnChange={handleGroupSelectionChange}
                    />
                  </>
                )}
                {(state.userTypes && state.userTypes.length > 0)
                && (
                  <>
                    <Accordion key={`userType_${accordionKey}`} defaultActiveKey={hasUserTypeFilterChanged ? "userTypeFilter" : ""}>
                      <Accordion.Item eventKey="userTypeFilter">
                        <Accordion.Header data-testid="userTypeFilter">User Types</Accordion.Header>
                        <Accordion.Body>
                          <section>
                            <Form.Group controlId="userType">
                              {renderUserTypes()}
                            </Form.Group>
                          </section>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </>
                )}
              </>
            )}

            <Divider />
          </div>
        </Form>
      </div>
    </FocusTrap>
  );
};

export default UserFilterDrawer;
